import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { w2d2dApiClient } from "../../w2d2d-api-client";

export interface ConnectionsControl {
    readonly connections: string[] | undefined;
    readonly refreshConnections: () => Promise<void>;
    readonly removeConnection: (userId: string) => void;
}

const ConnectionsControlContext = createContext<ConnectionsControl | undefined>(undefined);

interface ConnectionsControlProviderProps {
    children: ReactNode | ReactNode[];
}

export const ConnectionsControlProvider = (props: ConnectionsControlProviderProps) => {
    const connectionsControl = useConnectionsControlImpl();

    return (
        <ConnectionsControlContext.Provider value={connectionsControl}>
            {props.children}
        </ConnectionsControlContext.Provider>
    );
};

export function useConnectionsControl(): ConnectionsControl {
    const context = useContext(ConnectionsControlContext);
    if (context === undefined) {
        throw new Error('useConnectionsControl must be used within ConnectionsControlProvider');
    }
    return context;
}

function useConnectionsControlImpl(): ConnectionsControl {
    const [connections, setConnections] = useState<string[] | undefined>(undefined)

    const refreshConnections = useCallback(async () => {
        try {
            const response = await w2d2dApiClient.getConnections()
            setConnections(response.connections)
        } catch (err) {
            console.error("Failed to fetch connections:", err)
            // Ideally handle error state, but for now just log
        }
    }, [])

    const removeConnection = useCallback((userId: string) => {
        setConnections((current) => {
            if (!current) return current
            return current.filter(id => id !== userId)
        })
    }, [])

    useEffect(() => {
        let active = true
        w2d2dApiClient.getConnections()
            .then((response) => {
                if (active) {
                    setConnections(response.connections)
                }
            })
            .catch(err => console.error("Failed to load connections", err))

        return () => {
            active = false
        }
    }, [])

    return {
        connections,
        refreshConnections,
        removeConnection
    }
}

export default ConnectionsControlProvider
